import React, { useEffect, useState, useRef, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Calendar, FileText, Eye, Printer, Smartphone, ChevronLeft, ChevronRight } from 'lucide-react';
export function ProductShowcase() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const itemRefs = useRef<(HTMLLIElement | null)[]>([]);
  const productImages = ['https://cdn.magicpatterns.com/uploads/arvKWaAr2W8phk5hYZ5du2/5e3af1b2-0055-46ae-9be3-e9f8713490ad.png', 'https://cdn.magicpatterns.com/uploads/1Q39mF3VWT9PhkUkwA3HzT/2798d75f-b3cb-4bfa-ac26-f3b07864792c.png'];
  const items = ['Plantilla mensual de hábitos (núcleo del sistema)', 'Planificación semanal para ordenar la ejecución', 'Planificación diaria para bajar a acciones concretas', 'Sistema visual de seguimiento', 'Formato imprimible y reutilizable'];
  // Auto-advance carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex(prev => (prev + 1) % productImages.length);
    }, 4000); // Change image every 4 seconds
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const viewportCenter = window.innerHeight / 2;
          let closestIndex = null;
          let closestDistance = Infinity;
          itemRefs.current.forEach((ref, index) => {
            if (!ref) return;
            const rect = ref.getBoundingClientRect();
            const itemCenter = rect.top + rect.height / 2;
            const distance = Math.abs(itemCenter - viewportCenter);
            if (distance < closestDistance && distance < 100) {
              closestDistance = distance;
              closestIndex = index;
            }
          });
          setActiveIndex(closestIndex);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const nextImage = () => {
    setCurrentImageIndex(prev => (prev + 1) % productImages.length);
  };
  const prevImage = () => {
    setCurrentImageIndex(prev => (prev - 1 + productImages.length) % productImages.length);
  };
  return <section className="py-12 md:py-16 bg-[#0A0A0A] relative overflow-hidden" data-id="element-177">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 relative z-10" data-id="element-178">
        {/* Title */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="text-center mb-4" data-id="element-179">
          <h2 className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight leading-tight" data-id="element-180">
            Un{' '}
            <span className="text-[#FF6B35]" style={{
            textShadow: '0 0 20px rgba(255, 107, 53, 0.4)'
          }} data-id="element-181">
              sistema visual
            </span>{' '}
            que sí vas a seguir.
          </h2>
        </motion.div>

        {/* Subtitle */}
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="text-center text-gray-400 text-sm md:text-base mb-8" data-id="element-182">
          Diseñado para ejecutar, incluso cuando no tenés ganas.
        </motion.p>

        {/* Carousel */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="relative mb-6 flex justify-center" data-id="element-183">
          <div className="relative w-full max-w-3xl" data-id="element-184">
            {/* Image Container */}
            <div className="relative aspect-square overflow-hidden rounded-2xl" data-id="element-185">
              <AnimatePresence mode="wait" data-id="element-186">
                <motion.img key={currentImageIndex} src={productImages[currentImageIndex]} alt={`Sistema Enfocado - Vista ${currentImageIndex + 1}`} className="w-full h-full object-cover" initial={{
                opacity: 0,
                scale: 1.1
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.95
              }} transition={{
                duration: 0.5
              }} loading="lazy" data-id="element-187" />
              </AnimatePresence>
            </div>

            {/* Navigation Arrows */}
            <button onClick={prevImage} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white hover:bg-[#FF6B35] hover:border-[#FF6B35] transition-all duration-300 z-10" aria-label="Imagen anterior" data-id="element-188">
              <ChevronLeft size={20} data-id="element-189" />
            </button>
            <button onClick={nextImage} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white hover:bg-[#FF6B35] hover:border-[#FF6B35] transition-all duration-300 z-10" aria-label="Siguiente imagen" data-id="element-190">
              <ChevronRight size={20} data-id="element-191" />
            </button>

            {/* Dots Indicator */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10" data-id="element-192">
              {productImages.map((_, index) => <button key={index} onClick={() => setCurrentImageIndex(index)} className={`w-2 h-2 rounded-full transition-all duration-300 ${index === currentImageIndex ? 'bg-[#FF6B35] w-6' : 'bg-white/30 hover:bg-white/50'}`} aria-label={`Ir a imagen ${index + 1}`} data-id="element-193" />)}
            </div>
          </div>
        </motion.div>

        {/* Reinforcement Text */}
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.4
      }} className="text-center text-gray-500 text-sm md:text-base mb-12 max-w-2xl mx-auto px-8" data-id="element-194">
          Todo está pensado para sacarte el peso de la cabeza y llevarlo a un
          sistema claro y visible.
        </motion.p>

        {/* What's Included Section */}
        <div className="max-w-xl mx-auto px-8" data-id="element-195">
          <motion.h3 initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4
        }} className="text-sm font-medium text-gray-500 mb-6 uppercase tracking-wide" data-id="element-196">
            ¿Qué incluye el sistema?
          </motion.h3>

          <div className="relative pl-6" data-id="element-197">
            {/* Barra vertical neón naranja */}
            <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-[#FF6B35]/20 to-transparent" data-id="element-198" />

            <ul className="space-y-3" data-id="element-199">
              {items.map((item, index) => <li key={index} ref={el => itemRefs.current[index] = el} className="text-sm relative transition-colors duration-200" style={{
              color: activeIndex === index ? 'rgb(255, 107, 53)' : 'rgb(156, 163, 175)'
            }} data-id="element-200">
                  {/* Punto de activación en la barra */}
                  <div className="absolute -left-[25px] top-1/2 -translate-y-1/2 w-[6px] h-[6px] rounded-full transition-all duration-200" style={{
                backgroundColor: activeIndex === index ? 'rgb(255, 107, 53)' : 'transparent',
                opacity: activeIndex === index ? 1 : 0,
                transform: activeIndex === index ? 'translateY(-50%) scale(1)' : 'translateY(-50%) scale(0.5)',
                boxShadow: activeIndex === index ? '0 0 8px rgba(255, 107, 53, 0.6)' : 'none'
              }} data-id="element-201" />
                  {item}
                </li>)}
            </ul>
          </div>
        </div>
      </div>
    </section>;
}